import { useActionSheet } from '@expo/react-native-action-sheet'
import { createRef, memo, useCallback, useMemo, useState } from 'react'
import { Platform, View } from 'react-native'
import { StyleSheet, useUnistyles } from 'react-native-unistyles'

import DocumentEditModal from '@/components/fosters/documents/DocumentEditModal'
import UserAvatar from '@/components/navigation/UserAvatar'
import Alert from '@/components/shared/Alert'
import UniActivityIndicator from '@/components/unistyles/UniActivityIndicator'
import UniIcon from '@/components/unistyles/UniIcon'
import UniIconButton from '@/components/unistyles/UniIconButton'
import UniImage from '@/components/unistyles/UniImage'
import UniText from '@/components/unistyles/UniText'
import UniTouchableRipple from '@/components/unistyles/UniTouchableRipple'
import useFile from '@/hooks/useFile'
import { useAppStore } from '@/store/useAppStore'
import log from '@/utils/datadog/log/log'
import findNodeHandle from '@/utils/findNodeHandle'
import { logError } from '@/utils/log'
import trpc from '@/utils/trpc'
import { ApiRoot } from '../../../../constants'

const ASPECT_RATIO = 1.5
const MAX_HEIGHT = 400
const MAX_WIDTH = MAX_HEIGHT * ASPECT_RATIO

interface Props {
  cardWidth?: number
  createdAt?: Date
  detailShort?: string
  documentId: string
  fileName?: string
  fileId: string
  mimeType?: string
  testID?: string
  uploadedBy: {
    emailHash?: string | null
    firstName: string
    lastName: string
  }
  disabled?: boolean
  onUpdate: () => void
  onDelete: () => void
  fosterName?: string
  documentType?: string
}

const Document = ({
  cardWidth,
  createdAt,
  detailShort,
  documentId,
  fileId,
  fileName,
  mimeType,
  testID,
  uploadedBy,
  disabled,
  onUpdate,
  onDelete,
  fosterName,
  documentType,
}: Props) => {
  const { theme } = useUnistyles()
  const { showActionSheetWithOptions } = useActionSheet()
  const [isDownloading, setIsDownloading] = useState(false)
  const [isEditingModalOpen, setIsEditingModalOpen] = useState(false)
  const [imageLoadError, setImageLoadError] = useState(false)
  const [isLoadingThumbnail, setIsLoadingThumbnail] = useState(true)

  const accessToken = useAppStore.use.auth().accessToken

  const _anchorRef = createRef<any>()

  const currentOrganization = useAppStore.use.currentOrganization().organization

  // pre-warm the query for the edit modal
  trpc.foster.documents.byId.useQuery(
    {
      id: documentId,
    },
    {
      enabled: true,
    }
  )

  const { openFile, downloadFile } = useFile()

  const thumbnailUrl = useMemo(() => {
    if (!currentOrganization?.id || !fileId) return ''
    return `${ApiRoot()}/${currentOrganization.id}/${fileId}/download?width=${MAX_WIDTH * 2}&height=${MAX_HEIGHT * 2}`
  }, [currentOrganization?.id, fileId])

  const imageSource = useMemo(
    () => ({
      uri: thumbnailUrl,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }),
    [thumbnailUrl, accessToken]
  )

  const edit = () => {
    console.log('edit')
  }

  const open = useCallback(async () => {
    if (!currentOrganization?.id) {
      return
    }

    setIsDownloading(true)

    try {
      await openFile({
        fileName,
        fileId,
        mimeType,
        fosterName,
        documentType,
      })
    } catch (error: any) {
      log.error('There was a problem opening the file')
      logError(error)
    }

    setIsDownloading(false)
  }, [
    currentOrganization?.id,
    fileId,
    fileName,
    mimeType,
    fosterName,
    documentType,
    openFile,
  ])

  const download = useCallback(async () => {
    if (!currentOrganization?.id) {
      return
    }

    setIsDownloading(true)

    try {
      await downloadFile({
        fileName,
        fileId,
        mimeType,
        fosterName,
        documentType,
      })
    } catch (error: any) {
      log.error('There was a problem downloading the file')
      logError(error)
    }

    setIsDownloading(false)
  }, [
    currentOrganization?.id,
    fileId,
    fileName,
    mimeType,
    fosterName,
    documentType,
    downloadFile,
  ])

  const onLongPress = () => {
    const options = ['Edit', 'Delete', 'Cancel']
    const onEdit = 0
    const destructiveButtonIndex = 1
    const cancelButtonIndex = 2

    showActionSheetWithOptions(
      {
        anchor: findNodeHandle(_anchorRef.current) || undefined,
        useModal: false,
        options,
        cancelButtonIndex,
        destructiveButtonIndex,
      },
      (selectedIndex: number | undefined) => {
        switch (selectedIndex) {
          case onEdit:
            setIsEditingModalOpen(true)
            break
          case destructiveButtonIndex:
            Alert.alert(
              'Delete',
              'Are you sure you want to delete this document?',
              [
                {
                  text: 'Cancel',
                  onPress: () => console.log('Cancel Pressed'),
                  style: 'cancel',
                },
                {
                  isPreferred: false,
                  text: 'Delete',
                  onPress: () => onDelete(),
                  style: 'destructive',
                },
              ]
            )
            break

          case cancelButtonIndex:
            break
        }
      }
    )
  }

  return (
    <UniTouchableRipple
      accessibilityRole="button"
      borderless
      disabled={disabled || isDownloading}
      key={fileId}
      onLongPress={onLongPress}
      onPress={mimeType ? open : edit}
      ref={_anchorRef}
      style={[
        styles.root,
        cardWidth != null ? { width: cardWidth } : { flex: 1 },
      ]}
      testID={testID}
    >
      <View style={[{ padding: 16 }]}>
        <View style={styles.documentCardHeader}>
          <UserAvatar size={30} user={uploadedBy} />
          <View style={styles.documentCardHeaderContainer}>
            <UniText>
              {uploadedBy.firstName} {uploadedBy.lastName}
            </UniText>
            <View style={styles.headerRight}>
              <UniText>{createdAt?.toLocaleDateString()}</UniText>
              {Platform.OS === 'web' && (
                <UniIconButton
                  disabled={disabled || isDownloading}
                  icon="download"
                  iconColorName="onSurfaceVariant"
                  onPress={download}
                  size={18}
                />
              )}
            </View>
          </View>
        </View>
        <View style={styles.fileContainer}>
          <>
            {(isDownloading || isLoadingThumbnail) && (
              <UniActivityIndicator
                animating={true}
                size={MAX_HEIGHT / 10}
                style={styles.spinner}
              />
            )}
            {!imageLoadError ? (
              <UniImage
                accessibilityIgnoresInvertColors={true}
                allowDownscaling={true}
                cachePolicy={'disk'}
                contentFit="cover"
                key={fileId}
                onError={(_error) => {
                  setImageLoadError(true)
                  setIsLoadingThumbnail(false)
                }}
                onLoad={() => setIsLoadingThumbnail(false)}
                source={imageSource}
                style={{
                  ...StyleSheet.absoluteFillObject,
                  height: undefined,
                  width: undefined,
                }}
              />
            ) : (
              <UniIcon
                color={theme.colors.onSurfaceVariant}
                size={48}
                source={'image-broken'}
              />
            )}
          </>
        </View>
        {detailShort && detailShort.length > 0 && (
          <UniText style={styles.description} testID="document-detail-short">
            {detailShort}
          </UniText>
        )}
        <DocumentEditModal
          documentId={documentId}
          onClose={() => {
            setIsEditingModalOpen(false)
            onUpdate()
          }}
          open={isEditingModalOpen}
        />
      </View>
    </UniTouchableRipple>
  )
}

const styles = StyleSheet.create((theme) => {
  return {
    image: {
      ...StyleSheet.absoluteFillObject,
      height: undefined,
      width: undefined,
    },
    description: {
      marginTop: theme.tokens.spacing[3],
    },
    documentCardHeader: {
      alignItems: 'center',
      flexDirection: 'row',
      gap: theme.tokens.spacing[3],
      paddingBottom: 8,
    },
    documentCardHeaderContainer: {
      alignItems: 'center',
      flex: 1,
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    headerRight: {
      alignItems: 'center',
      flexDirection: 'row',
      gap: theme.tokens.spacing[1],
    },
    fileContainer: {
      alignItems: 'center',
      borderColor: theme.colors.cardBorder,
      borderRadius: 4,
      borderWidth: 1,
      height: MAX_HEIGHT / 2,
      justifyContent: 'center',
    },
    root: {
      backgroundColor: theme.colors.surface,
      borderColor: theme.colors.cardBorder,
      borderRadius: 8,
      borderWidth: 1,
      color: theme.colors.onBackground,
      flexGrow: 0,
      flexShrink: 0,
    },
    spinner: {
      position: 'absolute',
      top: MAX_HEIGHT / 4 - MAX_HEIGHT / 20,
      zIndex: 100,
    },
  }
})

export default memo(Document)
